import React from "react";
import styled, { css } from "styled-components";
import type { BracketPiece, FrameKind } from "../utils/frames";

// Frame marks shared by the AOTD Authors Leaderboard, the home game board and
// the /game standings. A marked row takes a flat fill, and a square bracket
// ("[") drawn just left of the table spans each run of marked rows. On wide
// screens a short label sits beside the bracket; on phones (and on /game, where
// the list fills its column) there is no room beside it, so the labels move to
// a small legend under the table, keyed by the same bracket.
// Colours come from the site palette (src/index.css): sky for a frame from the
// yearly leaderboard, pink for the game's frame. Both stay clear of the badge
// colours (gold, purple, indigo) and of the lemon "me" row on /game.

const COLORS: Record<FrameKind, { fill: string; line: string }> = {
  leaderboard: {
    fill: "var(--ui-toast-info, #a9e8ff)",
    line: "rgb(var(--ui-sky-hover, 29 147 211))",
  },
  game: {
    fill: "var(--ui-toast-error, #ffb0c2)",
    line: "rgb(var(--ui-pink-hover, 183 24 58))",
  },
};

/** The flat fill for a marked row. */
export function frameRowFill(kind: FrameKind | null | undefined): string | undefined {
  return kind ? COLORS[kind].fill : undefined;
}

// Phone layout: bracket only, labels in the legend, just enough left padding
// for the bracket.
const compact = css<{ $marked: boolean }>`
  --fm-gap: 11px;
  ${(p) =>
    p.$marked &&
    css`
      padding-left: 14px;
    `}

  .fm-label {
    display: none;
  }

  .fm-legend {
    display: flex;
  }
`;

/**
 * Wraps a marked table (or list). $compact uses the phone layout at every
 * width, for a list that fills its column.
 */
export const FrameArea = styled.div<{ $marked: boolean; $compact?: boolean }>`
  --fm-gap: 12px;
  width: 100%;
  box-sizing: border-box;

  .fm-legend {
    display: none;
  }

  ${(p) => p.$compact && compact}

  @media (max-width: 640px) {
    ${compact}
  }
`;

const Line = styled.span<{ $kind: FrameKind; $top: boolean; $bottom: boolean }>`
  position: absolute;
  left: calc(-1 * var(--fm-gap, 12px));
  width: 7px;
  top: ${(p) => (p.$top ? "4px" : "-1px")};
  bottom: ${(p) => (p.$bottom ? "4px" : "-1px")};
  border-left: 3px solid ${(p) => COLORS[p.$kind].line};
  border-top: ${(p) => (p.$top ? `3px solid ${COLORS[p.$kind].line}` : "0")};
  border-bottom: ${(p) => (p.$bottom ? `3px solid ${COLORS[p.$kind].line}` : "0")};
  box-sizing: border-box;
  filter: drop-shadow(1.5px 1.5px 0 rgba(0, 0, 0, 0.7));
  pointer-events: none;
`;

const Label = styled.span<{ $at: "middle" | "bottom" }>`
  position: absolute;
  right: calc(100% + var(--fm-gap, 12px) + 5px);
  top: ${(p) => (p.$at === "middle" ? "50%" : "100%")};
  transform: translateY(-50%);
  width: 132px;
  font-size: 11px;
  line-height: 1.2;
  font-weight: 700;
  text-align: right;
  white-space: normal;
  color: #111;
  pointer-events: none;
`;

const SrOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;

/**
 * One row's slice of a bracket, placed in the row's first cell, which must be
 * position: relative. The visible label is decoration; every marked row also
 * carries the text for screen readers.
 */
export function FrameBracket({ piece, srText }: { piece: BracketPiece; srText: string }): JSX.Element {
  return (
    <>
      <Line
        aria-hidden
        data-testid={`frame-bracket-${piece.kind}`}
        $kind={piece.kind}
        $top={piece.top}
        $bottom={piece.bottom}
      />
      {piece.label ? (
        <Label aria-hidden className="fm-label" data-testid={`frame-label-${piece.kind}`} $at={piece.labelAt}>
          {piece.label}
        </Label>
      ) : null}
      <SrOnly>{srText}</SrOnly>
    </>
  );
}

const LegendRow = styled.div`
  flex-wrap: wrap;
  justify-content: center;
  gap: 4px 14px;
  margin: -12px 0 14px;
  font-size: 0.72rem;
  font-weight: 600;
  color: #1a1a1a;
`;

const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
`;

const MiniBracket = styled.span<{ $kind: FrameKind }>`
  display: inline-block;
  width: 5px;
  height: 13px;
  border: 2.5px solid ${(p) => COLORS[p.$kind].line};
  border-right: 0;
  box-sizing: border-box;
  filter: drop-shadow(1px 1px 0 rgba(0, 0, 0, 0.7));
`;

/**
 * The labels under the table, shown only in the phone layout (FrameArea hides
 * it otherwise). Lists each kind present, in the order given.
 */
export function FrameLegend({
  kinds,
  labels,
  style,
}: {
  kinds: Array<FrameKind | null>;
  labels: Record<FrameKind, string>;
  style?: React.CSSProperties;
}): JSX.Element | null {
  const present = (["leaderboard", "game"] as FrameKind[]).filter((k) => kinds.includes(k));
  if (present.length === 0) return null;
  return (
    <LegendRow className="fm-legend" aria-hidden style={style}>
      {present.map((k) => (
        <LegendItem key={k}>
          <MiniBracket $kind={k} />
          {labels[k]}
        </LegendItem>
      ))}
    </LegendRow>
  );
}

export const FrameNote = styled.p`
  margin: -8px 0 12px;
  font-size: 0.72rem;
  font-weight: 500;
  color: #1a1a1a;
  text-align: center;
`;
